import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Sparkles, Save, X, Image as ImageIcon, Video, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { T } from '../theme/radar';

interface Mancante {
  id: string;
  title: string;
  content_type: 'reel' | 'post';
  post_formato?: 'grafica' | 'foto' | null;
  status: string;
  published_date: string | null;
  conferma_cliente: string | null;
  hook: string | null;
  thumbnail_url: string | null;
}

interface Props {
  clientId: string;
  clientName: string;
  /** Quanti ne mancano, per il numero sulla scheda. */
  onConteggio?: (n: number) => void;
}

const LIMITE = 2200;
const caratteri = (s: string) => Array.from(s).length;
const hashtag = (s: string) => (s.match(/(^|\s)#[^\s#]+/g) || []).length;
const quando = (s: string | null) =>
  s
    ? new Date(s).toLocaleString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' })
    : 'senza data';

/**
 * Caption mancanti: i contenuti che stanno per uscire (programmati, o
 * confermati dal cliente nel gruppo) senza la caption. È la stessa caption
 * che pubblica manda a Metricool così com'è: senza, il contenuto non esce.
 *
 * «Scrivi caption» chiede una proposta all'AI (POST /api/ai/caption), che qui
 * si corregge e si salva col salvataggio normale del contenuto. Niente si
 * salva da solo. I clienti con i testi scritti dal cliente non passano da
 * qui: la caption la incolla chi riceve il testo.
 */
const CaptionMancanti: React.FC<Props> = ({ clientId, clientName, onConteggio }) => {
  const { getToken } = useAuth();
  const { state, updateContenuto } = useData();
  const [elenco, setElenco] = useState<Mancante[]>([]);
  const [testiDelCliente, setTestiDelCliente] = useState(false);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [bozze, setBozze] = useState<Record<string, string>>({});
  const [avvisi, setAvvisi] = useState<Record<string, string[]>>({});
  const [scrivendo, setScrivendo] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);

  const carica = useCallback(async () => {
    setInCaricamento(true);
    setErrore(null);
    try {
      const res = await fetch(`/api/ai/caption-mancanti?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) throw new Error(json?.message || 'Non riesco a leggere le caption mancanti');
      setElenco(json.data.contenuti);
      setTestiDelCliente(!!json.data.testi_dal_cliente);
      onConteggio?.(json.data.contenuti.length);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Errore');
    } finally {
      setInCaricamento(false);
    }
  }, [clientId, getToken, onConteggio]);

  useEffect(() => {
    carica();
  }, [carica]);

  const scrivi = async (c: Mancante) => {
    setScrivendo(c.id);
    try {
      const res = await fetch('/api/ai/caption', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_id: c.id }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) throw new Error(json?.message || 'L\'AI non ha risposto');
      setBozze((b) => ({ ...b, [c.id]: json.data.caption }));
      setAvvisi((a) => ({ ...a, [c.id]: json.data.avvisi || [] }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Errore nella scrittura della caption');
    } finally {
      setScrivendo(null);
    }
  };

  const salva = async (c: Mancante) => {
    const testo = (bozze[c.id] || '').trim();
    if (!testo) return;
    setSalvando(c.id);
    try {
      // Il salvataggio normale del contenuto. Se il contenuto è fra quelli
      // caricati nella pagina passa dal contesto (così le schede si
      // aggiornano subito), altrimenti lo stesso PUT diretto.
      if (state.contenuti.some((x) => x.id === c.id)) {
        await updateContenuto(c.id, { copyPost: testo });
      } else {
        const res = await fetch(`/api/contents/${c.id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ post_copy: testo }),
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.success) throw new Error(json?.message || 'Salvataggio non riuscito');
      }
      toast.success(`Caption salvata: ${c.title}`);
      const resto = elenco.filter((x) => x.id !== c.id);
      setElenco(resto);
      onConteggio?.(resto.length);
      setBozze(({ [c.id]: _tolta, ...altre }) => altre);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Salvataggio non riuscito');
    } finally {
      setSalvando(null);
    }
  };

  if (inCaricamento) {
    return (
      <div className="flex items-center gap-2 py-10 justify-center text-sm" style={{ color: T.inkMuted }}>
        <Loader2 size={16} className="animate-spin" /> Cerco i contenuti senza caption
      </div>
    );
  }

  if (errore) {
    return (
      <div className="py-6 text-center text-sm rounded-xl" style={{ color: T.danger, background: T.surface, border: `1px solid ${T.borderSoft}` }}>
        {errore}{' '}
        <button className="underline" style={{ color: T.ink }} onClick={carica}>Riprova</button>
      </div>
    );
  }

  if (testiDelCliente) {
    return (
      <div className="py-10 px-4 text-center text-sm rounded-xl max-w-3xl" style={{ color: T.inkMuted, background: T.surface, border: `1px solid ${T.borderSoft}` }}>
        I testi di {clientName} li scrive il cliente: la caption va incollata nel contenuto quando arriva.
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <p className="text-sm mb-4" style={{ color: T.inkMuted }}>
        Contenuti di {clientName} programmati o confermati dal cliente senza caption. Senza caption pubblica non li manda in uscita.
      </p>

      {elenco.length === 0 ? (
        <div className="py-10 text-center text-sm rounded-xl flex items-center justify-center gap-2" style={{ color: T.inkMuted, background: T.surface, border: `1px solid ${T.borderSoft}` }}>
          <CheckCircle size={16} color={T.success} /> Nessuna caption mancante.
        </div>
      ) : (
        <div className="space-y-3">
          {elenco.map((c) => {
            const bozza = bozze[c.id];
            const n = bozza !== undefined ? caratteri(bozza) : 0;
            const h = bozza !== undefined ? hashtag(bozza) : 0;
            const fuori = n > LIMITE || h > 30;
            return (
              <div key={c.id} className="rounded-xl p-3 sm:p-4" style={{ background: T.surface, border: `1px solid ${T.borderSoft}` }}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center" style={{ background: T.sunken }}>
                    {c.content_type === 'post' ? <ImageIcon size={16} color={T.inkMuted} /> : <Video size={16} color={T.inkMuted} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold break-words" style={{ color: T.ink }}>{c.title}</div>
                    <div className="text-xs mt-0.5" style={{ color: T.inkMuted }}>
                      {c.content_type === 'post' ? (c.post_formato === 'foto' ? 'Carosello foto' : 'Post grafico') : 'Reel'} · {c.status === 'scheduled' ? `esce ${quando(c.published_date)}` : 'confermato dal cliente, da programmare'}
                    </div>
                    {c.hook && <div className="text-xs mt-1 line-clamp-2 break-words" style={{ color: T.inkFaint }}>{c.hook}</div>}
                  </div>
                  {bozza === undefined && (
                    <button
                      onClick={() => scrivi(c)}
                      disabled={scrivendo !== null}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-medium rounded-lg px-3 min-h-[40px] disabled:opacity-50"
                      style={{ background: T.accent, color: T.onAccent, border: `1px solid ${T.ink}` }}
                    >
                      {scrivendo === c.id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      <span className="hidden sm:inline">{scrivendo === c.id ? 'Scrivo' : 'Scrivi caption'}</span>
                    </button>
                  )}
                </div>

                {bozza !== undefined && (
                  <div className="mt-3">
                    <textarea
                      value={bozza}
                      onChange={(e) => setBozze((b) => ({ ...b, [c.id]: e.target.value }))}
                      rows={Math.min(14, Math.max(5, bozza.split('\n').length + 1))}
                      className="w-full text-sm rounded-lg p-3 resize-y"
                      style={{ border: `1px solid ${fuori ? T.danger : T.border}`, background: T.surface, color: T.ink }}
                      aria-label={`Caption di ${c.title}`}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                      <span className="text-xs tabular-nums" style={{ color: fuori ? T.danger : T.inkFaint }}>
                        {n.toLocaleString('it-IT')}/{LIMITE.toLocaleString('it-IT')} caratteri · {h} hashtag
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => setBozze(({ [c.id]: _tolta, ...altre }) => altre)}
                          className="inline-flex items-center gap-1 whitespace-nowrap text-sm rounded-lg px-3 min-h-[40px]"
                          style={{ border: `1px solid ${T.border}`, color: T.ink, background: T.surface }}
                        >
                          <X size={14} /> Scarta
                        </button>
                        <button
                          onClick={() => scrivi(c)}
                          disabled={scrivendo !== null}
                          className="inline-flex items-center gap-1 whitespace-nowrap text-sm rounded-lg px-3 min-h-[40px] disabled:opacity-50"
                          style={{ border: `1px solid ${T.border}`, color: T.ink, background: T.surface }}
                        >
                          {scrivendo === c.id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Riscrivi
                        </button>
                        <button
                          onClick={() => salva(c)}
                          disabled={salvando !== null || !bozza.trim() || fuori}
                          className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium rounded-lg px-3 min-h-[40px] disabled:opacity-50"
                          style={{ background: T.ink, color: T.surface }}
                        >
                          {salvando === c.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salva caption
                        </button>
                      </div>
                    </div>
                    {(avvisi[c.id] || []).length > 0 && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs" style={{ color: T.warning }}>
                        <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                        <span>Da sistemare prima di salvare: {(avvisi[c.id] || []).join('; ')}.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CaptionMancanti;
